<template>
    <div class="calendar-input">
        <div class="header">
            <i class="material-icons" @click="previousMonth">chevron_left</i>
            <span>{{ title }}</span>
            <i class="material-icons" @click="nextMonth">chevron_right</i>
        </div>
        <div class="days">
            <div :data-day="0">Su</div>
            <div :data-day="1">Mo</div>
            <div :data-day="2">Tu</div>
            <div :data-day="3">We</div>
            <div :data-day="4">Th</div>
            <div :data-day="5">Fr</div>
            <div :data-day="6">Sa</div>
            <div
                v-for="date in visible"
                :class="{
                    filler: date.getMonth() !== month,
                    today: date.valueOf() === today.valueOf(),
                    start: isStart(date),
                    mid: isMid(date),
                    end: isEnd(date),
                }"
                :data-date="date.getDate()"
                :key="date.valueOf()"
                @click="select(date)"
            >
                {{ date.getDate() }}
            </div>
        </div>
    </div>
</template>
<script>
import { addDays, addMonths, getMonthName, startOfDay, startOfMonth, treatUtcAsLocal } from '@/utils/date'
import { iso8601Date } from '@/utils/format'

export default {
    props: {
        startDate: {
            type: String,
        },
        endDate: {
            type: String,
        },
    },
    data() {
        return {
            start: startOfMonth(),
            selecting: 'startDate',
        }
    },
    computed: {
        endDateAsDate() {
            return this.endDate ? treatUtcAsLocal(new Date(this.endDate)) : null
        },
        startDateAsDate() {
            return this.startDate ? treatUtcAsLocal(new Date(this.startDate)) : null
        },
        end() {
            return addMonths(1, this.start)
        },
        isStart() {
            const startDate = this.startDateAsDate?.valueOf() ?? null

            if (startDate) {
                return date => date.valueOf() === startDate
            }

            return () => false
        },
        isMid() {
            const startDate = this.startDateAsDate ?? null
            const endDate = this.endDateAsDate ?? null

            if (startDate && endDate) {
                return date => date > this.startDateAsDate && date < this.endDateAsDate
            }

            return () => false
        },
        isEnd() {
            const endDate = this.endDateAsDate?.valueOf() ?? null

            if (endDate) {
                return date => date.valueOf() === endDate
            }

            return () => false
        },
        month() {
            return this.start.getMonth()
        },
        title() {
            return `${getMonthName(this.start)} ${this.start.getFullYear()}`
        },
        today() {
            return startOfDay()
        },
        visible() {
            const start = this.start
            const end = this.end

            const visible = []

            let date = addDays(-start.getDay(), start)

            while (date < end) {
                visible.push(date)
                date = addDays(1, date)
            }

            while (date.getDay() != 0) {
                visible.push(date)
                date = addDays(1, date)
            }

            return visible
        },
    },
    methods: {
        nextMonth() {
            this.start = addMonths(1, this.start)
        },
        previousMonth() {
            this.start = addMonths(-1, this.start)
        },
        select(date) {
            date = iso8601Date(date)

            if (this.selecting === 'startDate') {
                if (date > this.endDate) {
                    this.$emit('update:endDate', date)
                }
                this.$emit('update:startDate', date)
                this.selecting = 'endDate'
            } else {
                if (date < this.startDate) {
                    this.$emit('update:startDate', date)
                }
                this.$emit('update:endDate', date)
                this.selecting = 'startDate'
            }
        },
    },
}
</script>
<style scoped>
.calendar-input {
    display: flex;
    gap: 8px;
    flex-direction: column;
    padding-bottom: 42px;
    user-select: none;

    .header {
        align-items: center;
        display: grid;
        grid-template-columns: repeat(7, 42px);
        width: 100%;

        .material-icons {
            flex-grow: 0;
            flex-basis: 0;
            cursor: pointer;
        }

        span {
            grid-column: 2 / 7;
            flex-grow: 1;
            font-size: 16px;
            text-align: center;
        }
    }

    .days {
        align-items: center;
        display: grid;
        grid-template-columns: repeat(7, 42px);
        grid-template-rows: auto;
        grid-auto-rows: 42px;
        row-gap: 2.5%;
        vertical-align: middle;
        width: 100%;
    }

    [data-day],
    [data-date] {
        text-align: center;
    }

    [data-day] {
        font-weight: bold;
    }

    .filler {
        color: var(--color-primary-300);
    }

    [data-date] {
        position: relative;
        line-height: 24px;
        font-size: 14px;
    }

    .today {
        background-color: black;
        color: white;
        z-index: -1;
    }

    .start {
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
        border-top: 1px solid var(--color-dark);
        border-left: 1px solid var(--color-dark);
        border-bottom: 1px solid var(--color-dark);
    }

    .mid {
        border-top: 1px solid var(--color-dark);
        border-bottom: 1px solid var(--color-dark);
    }

    .end {
        border-top-right-radius: 12px;
        border-bottom-right-radius: 12px;
        border-top: 1px solid var(--color-dark);
        border-right: 1px solid var(--color-dark);
        border-bottom: 1px solid var(--color-dark);
    }
}
</style>
